import React, { useState, useContext } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions, TextField,
    makeStyles, Typography, Divider, Box, CircularProgress, Grid,
} from '@material-ui/core';
import Button from "components/CustomButtons/Button.js";
import MDEditor from "@uiw/react-md-editor";

// Styles
import styles from "assets/jss/material-dashboard-pro-react/views/dashboardStyle.js";

// Contexts
import NotificationContext from "views/Components/Context.js";

// Custom Components
import {
    scrapeAPI,
    addResourceToLabAPI,
} from "views/APIs/APIUtility";
import MarkdownEditorWithPreview from '../../../components/MarkdownEditor/MarkdownEditorWithPreview';

const useStyles = makeStyles((theme) => ({
    ...styles,
}));

export default function ScrapperModal({ open, onClose, labID, handleSetFileResources }) {
    const classes = useStyles();
    const { showNotification } = useContext(NotificationContext);
    const [linkValue, setLinkValue] = useState("");
    const [download, setDownload] = useState(false);
    const [loading, setLoading] = useState({ loading: false, message: "" });
    const [error, setError] = useState({ error: false, message: "" });
    const [markdownResource, setMarkdownResource] = useState({ name: "", description: "", link: null, content: "The content extracted from the provided link will be displayed here once available." });

    const handleScrapping = async () => {
        if (!validateLink(linkValue)) return;

        setLoading({ loading: true, message: "Scraping..." });
        setError({ error: false, message: "" });
        try {
            const links = [];
            links.push(linkValue);

            const requestData = {
                project_id: labID,
                start_urls: links
            };
            const response = await scrapeAPI(requestData);
            // Don't reassign 'response', instead assign 'response.project' to a new variable
            const project = response.project;
            const scraped_data_files = project["scraped_data_files"][0];  // Access the first file
            if (scraped_data_files["success"] !== 1) {
                setError({
                    error: true,
                    message: "We were unable to extract content from the provided link. The link may be invalid or unsupported at this time. Please try again later."
                });
                return;
            }

            setMarkdownResource({
                name: scraped_data_files.file_name,
                description: "Markdown file added using scraping.",
                link: scraped_data_files.s3_link,
                content: scraped_data_files.markdown_content,
            });

            setError({ error: false, message: "Below are the results of scraping the link. You can download the content or save it to your collection." });
            setDownload(true); // Enable download button after successful scraping
        }
        catch (err) {
            setError({ error: true, message: "Error scraping the link" });
        }
        finally {
            setLoading({ loading: false, message: "" });
        }
    };

    const handleDownload = async () => {
        // Ensure the link is available before attempting to download
        if (!markdownResource.link) {
            setError({ error: true, message: "No link available to download" });
            return;
        }

        try {
            // Create a link element
            const link = document.createElement("a");
            link.href = markdownResource.link; // Use the link from markdownResource
            link.download = markdownResource.name || "scraped_content.md"; // Set a default filename

            // Trigger the download
            link.click();

            // Reset any previous error messages
            setError({ error: false, message: "" });
        } catch (err) {
            setError({ error: true, message: "Failed to download the content" });
        }
    };


    const handleSave = async () => {
        setLoading({ isLoading: true, message: "Saving File..." });
        try {
            const formData = new FormData();

            // Convert markdown content to a Blob and then to a File
            const markdownBlob = new Blob([markdownResource.content], { type: "text/markdown" });
            const fileName = `${markdownResource.name || "resource"}.md`;
            const markdownFile = new File([markdownBlob], fileName, { type: "text/markdown" });

            // Add fields to FormData
            formData.append("lab_id", labID);
            formData.append("resource_type", "text/markdown");
            formData.append("resource_name", markdownResource.name);
            formData.append("resource_description", markdownResource.description);
            // formData.append("resource_file", markdownResource.link); // Add the link to FormData
            formData.append("resource_file", markdownFile);

            const response = await addResourceToLabAPI(formData);

            handleSetFileResources(response.raw_resources);
            showNotification("File Added successfully", "success");
            _onClose();
        } catch (error) {
            showNotification(
                `Failed to replace resource: ${error.message || "Unknown error"}`,
                "danger"
            );
        } finally {
            setLoading({ isLoading: false, message: "" });
        }
    };


    const validateLink = (value) => {
        const urlRegex = /^(https?:\/\/)?([\w-]+\.)+[\w-]{2,}(\/[\w-./?%&=]*)?$/i;
        if (!value) {
            setError({ error: true, message: "Link is required" });
            return false;
        }
        if (!urlRegex.test(value)) {
            setError({ error: true, message: "Enter a valid URL" });
            return false;
        }
        return true;
    };

    const _onClose = () => {
        setLinkValue("");
        setMarkdownResource({ name: "", description: "", link: null, content: "The content extracted from the provided link will be displayed here once available." });
        setDownload(false);
        setError({ error: false, message: "" });
        setLoading({ loading: false, message: "" });
        onClose();
    }


    return (
        <Dialog open={open} onClose={_onClose} fullWidth maxWidth="md">
            <DialogTitle> Add Link </DialogTitle>
            <Divider />
            <DialogContent>
                {loading.loading ? (
                    <Box
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                        minHeight="60vh"
                    >
                        <CircularProgress className={classes.loaderClass} /> &nbsp;
                        <Typography variant="h6" style={{ textTransform: 'none', marginLeft: 16 }}>
                            {loading.message}
                        </Typography>
                    </Box>
                ) : (
                    <Grid container spacing={2} direction="column">
                        <Grid item>
                            <TextField
                                autoFocus
                                margin="dense"
                                label="Link"
                                type="text"
                                fullWidth
                                value={linkValue}
                                onChange={(e) => {
                                    setLinkValue(e.target.value);
                                    setError({ error: false, message: "" });
                                }}
                            />
                        </Grid>

                        {error.message && (
                            <Grid item>
                                <Typography
                                    color={error.error ? "error" : "success"}
                                    variant="body2"
                                    sx={{ ml: 0.5 }}
                                >
                                    {error.message}
                                </Typography>
                            </Grid>
                        )}

                        {download && (
                            <Grid item>
                                <div data-color-mode="light">
                                    <MarkdownEditorWithPreview
                                        value={markdownResource.content}
                                        height={250}
                                        disabled={true}
                                    />
                                </div>
                            </Grid>
                        )}

                    </Grid>
                )}
            </DialogContent>





            <DialogActions>
                <Grid container alignItems="center">
                    {/* Left: Cancel button */}
                    <Grid item xs={12} sm={6}>
                        <Box display="flex" justifyContent="flex-start" marginLeft="15px">
                            <Button onClick={_onClose} color="quCreateColor" disabled={loading.loading}>
                                Cancel
                            </Button>
                        </Box>
                    </Grid>

                    {/* Right: Other buttons */}
                    <Grid item xs={12} sm={6}>
                        <Box display="flex" justifyContent="flex-end" gap={1} marginRight="15px">
                            <Button color="quCreateColor" onClick={handleScrapping} disabled={loading.loading}>
                                Scrape Link
                            </Button>
                            <Button color="quCreateColor" disabled={!download || loading.loading} onClick={handleDownload}>
                                Download
                            </Button>
                            <Button color="quCreateColor" disabled={!download || loading.loading} onClick={handleSave}>
                                Use Scraped Content
                            </Button>
                        </Box>
                    </Grid>
                </Grid>
            </DialogActions>


        </Dialog >
    );
}
